import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Permissions are derived from the real JWT roles carried in
 * `useAuth().user.roles`. The CMS portal only ever sees the two internal
 * Athlyn roles below — there is no separate `role`/`permissions` field on the
 * user, and the previous fake role models (admin / corporate_client /
 * tenant_admin / editor) have been removed.
 */
export const ATHLYN_ADMIN_ROLE = 'AthlynAdmin' as const;
export const ATHLYN_TEAM_ROLE = 'AthlynTeam' as const;

export type Permission =
  | 'view_corporate_clients'
  | 'manage_corporate_clients'
  | 'view_tenants'
  | 'manage_tenants'
  | 'view_marketplace'
  | 'manage_marketplace'
  | 'marketplace:read'
  | 'marketplace:write'
  | 'view_financials'
  | 'manage_financials'
  | 'view_analytics'
  | 'view_support'
  | 'manage_support'
  // Admin-only permissions (AthlynAdmin only — AthlynTeam never gets these)
  | 'admin_access'
  | 'manage_billing'
  | 'manage_translations'
  | 'manage_customizations'
  | 'view_design_system';

/** Permissions granted ONLY to AthlynAdmin (AthlynTeam is denied these). */
const ADMIN_ONLY_PERMISSIONS: readonly Permission[] = [
  'admin_access',
  'manage_billing',
  'manage_translations',
  'manage_customizations',
  'view_design_system',
];

/** Every permission AthlynAdmin holds (the full set). */
const ALL_PERMISSIONS: readonly Permission[] = [
  'view_corporate_clients',
  'manage_corporate_clients',
  'view_tenants',
  'manage_tenants',
  'view_marketplace',
  'manage_marketplace',
  'marketplace:read',
  'marketplace:write',
  'view_financials',
  'manage_financials',
  'view_analytics',
  'view_support',
  'manage_support',
  ...ADMIN_ONLY_PERMISSIONS,
];

/** Permissions AthlynTeam holds: everything except the admin-only set. */
const TEAM_PERMISSIONS: readonly Permission[] = ALL_PERMISSIONS.filter(
  (p) => !ADMIN_ONLY_PERMISSIONS.includes(p)
);

/**
 * Sidebar menu items (by `id`) that are restricted to AthlynAdmin. AthlynTeam
 * sees every other item; a user with neither role sees none. Mapped by `id`
 * (not label) so renaming a label can never silently change access.
 */
export const ADMIN_ONLY_MENU_IDS: readonly string[] = [
  'admin', // System Administration
  'billing', // Global Billing Dashboard
  'design-system', // Design System
  'components', // Components & Library
  'localization', // Localization Dashboard
  'customizations', // Tenant Customizations
];

function rolesToPermissions(roles: readonly string[]): Permission[] {
  if (roles.includes(ATHLYN_ADMIN_ROLE)) return [...ALL_PERMISSIONS];
  if (roles.includes(ATHLYN_TEAM_ROLE)) return [...TEAM_PERMISSIONS];
  return [];
}

export default function usePermissions() {
  const { user } = useAuth();

  const roles = useMemo<string[]>(() => user?.roles ?? [], [user]);

  const isAthlynAdmin = useMemo(() => roles.includes(ATHLYN_ADMIN_ROLE), [roles]);
  const isAthlynTeam = useMemo(() => roles.includes(ATHLYN_TEAM_ROLE), [roles]);

  const permissions = useMemo<Permission[]>(() => rolesToPermissions(roles), [roles]);

  const hasPermission = (permission: Permission): boolean =>
    permissions.includes(permission);

  const hasAnyPermission = (permissionList: Permission[]): boolean =>
    permissionList.some((p) => permissions.includes(p));

  const hasAllPermissions = (permissionList: Permission[]): boolean =>
    permissionList.every((p) => permissions.includes(p));

  /**
   * Whether the current user may see/access a sidebar menu item by its `id`.
   * AthlynAdmin sees everything; AthlynTeam sees everything except the
   * admin-only ids; anyone else sees nothing.
   */
  const canAccessMenu = (menuId: string): boolean => {
    if (isAthlynAdmin) return true;
    if (isAthlynTeam) return !ADMIN_ONLY_MENU_IDS.includes(menuId);
    return false;
  };

  return {
    permissions,
    roles,
    isAthlynAdmin,
    isAthlynTeam,
    isAdmin: isAthlynAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessMenu,
  };
}
